/**
 * Presentation helpers for the Events workspace (#898), the Event Overview
 * (#899) and My Outreach (#888).
 *
 * Every number these surfaces show comes from the server projections in
 * `eventOutreachApi.js`. This module only turns server values into words,
 * dates and links: it never counts, filters, or decides scope.
 */

// #1013: Outreach, Attendees and Follow-up became one Guests tab; Activity is the event's log.
export const EVENT_TABS = ['overview', 'guests', 'activity', 'photos', 'face_review'];

/** Tabs that are shown but cannot be opened. Face review is paused until #967's scope is agreed (#1013, OWN-13). */
export const DISABLED_EVENT_TABS = ['face_review'];

/** The tab keys that existed before the Guests tab; old links and older API targets still carry them (#1013). */
export const LEGACY_EVENT_TABS = ['outreach', 'attendees', 'follow_up'];

/** Filters an Event Detail deep link may carry into the Guests tab. `stage` is the pre-Guests spelling of `column`. */
export const OUTREACH_LINK_FILTERS = ['owner_id', 'follow_up', 'column', 'stage', 'rsvp_status'];

// The old outreach stage a `?stage=` link named -> the Guests column that now holds those guests (#1013, decision page 3).
const LEGACY_STAGE_COLUMNS = {
    to_invite: 'to_invite', invited: 'invited', responded: 'invited',
    thank_you: 'following_up', following_up: 'following_up', call_booked: 'meeting_booked',
    handed_to_sales: 'converted', not_interested: 'closed',
};

/**
 * The tab and filters an old deep link means in the Guests world: a pre-#1013 tab key opens `guests`, and a legacy
 * `stage` filter becomes the `column` filter. A stage with no column of its own is dropped; an explicit `column` wins.
 */
export function normalizeTabLink(tab, filters = {}) {
    const next = { ...(filters || {}) };
    if (next.stage) {
        if (!next.column && LEGACY_STAGE_COLUMNS[next.stage]) next.column = LEGACY_STAGE_COLUMNS[next.stage];
        delete next.stage;
    }
    return { tab: LEGACY_EVENT_TABS.includes(tab) ? 'guests' : tab, filters: next };
}

/**
 * A metric by its #1013 name, reading the old key a one-release-old API still sends: `meetings_booked` was
 * `calls_booked` and `converted` was `handed_to_sales` (P10).
 */
const METRIC_ALIASES = { meetings_booked: 'calls_booked', converted: 'handed_to_sales' };
export const metricValue = (metrics, key) => {
    const value = metrics?.[key] ?? (METRIC_ALIASES[key] ? metrics?.[METRIC_ALIASES[key]] : undefined);
    return value ?? 0;
};

/**
 * Whether the viewer may Start, End, Reopen or Reschedule an event and change its details (D9). The server
 * aliases `events:update` to `activities:update` for organizations that predate the event vocabulary, but
 * `/auth/permissions` lists only the stored names, so the alias is mirrored here.
 */
export const canUpdateEvents = (has) => Boolean(has('events:update') || has('activities:update'));

export const STATUS_CHIP = {
    draft: 'bg-stone-100 text-stone-600 border-stone-200',
    published: 'bg-blue-50 text-blue-700 border-blue-100',
    completed: 'bg-stone-100 text-stone-600 border-stone-200',
    cancelled: 'bg-red-50 text-red-700 border-red-100',
};

export const STATUS_LABELS = { draft: 'Draft', published: 'Published', completed: 'Completed', cancelled: 'Cancelled' };

export const MODALITY_LABELS = { in_person: 'In person', online: 'Online', hybrid: 'Hybrid' };

const PLATFORM_LABELS = { zoom: 'Zoom', velocity: 'Velocity' };

/** The one primary action per card phase, and the Event Detail tab it opens (#898). */
export const PHASE_ACTIONS = {
    draft: { label: 'Publish', tab: null, publish: true },
    upcoming: { label: 'Open guests', tab: 'guests' },
    today: { label: 'Start check-in', tab: 'guests' },
    attendance: { label: 'Mark attendance', tab: 'guests' },
    followup: { label: 'Open guests', tab: 'guests' },
    done: { label: 'View results', tab: 'overview', secondary: true },
    cancelled: { label: 'View', tab: 'overview', secondary: true },
};

export const PHASE_CHIP = {
    draft: ['Draft', 'bg-stone-100 text-stone-600 border-stone-200'],
    upcoming: ['Outreach', 'bg-blue-50 text-blue-700 border-blue-100'],
    today: ['Today', 'bg-stone-900 text-white border-stone-900'],
    attendance: ['Attendance', 'bg-amber-50 text-amber-800 border-amber-200'],
    followup: ['Follow-up', 'bg-blue-50 text-blue-700 border-blue-100'],
    done: ['Done', 'bg-emerald-50 text-emerald-700 border-emerald-100'],
    cancelled: ['Cancelled', 'bg-red-50 text-red-700 border-red-100'],
};

/** `/events/:id?tab=…&filter=…` — the URL an Event Detail tab is restored from after a refresh. */
export function eventTabHref(eventId, tab, filters = {}) {
    // An old tab key (from an older API target or a saved link) is written as the Guests tab (#1013).
    const link = normalizeTabLink(tab, filters);
    const params = new URLSearchParams();
    if (link.tab) params.set('tab', link.tab);
    OUTREACH_LINK_FILTERS.forEach((key) => {
        if (link.filters[key]) params.set(key, String(link.filters[key]));
    });
    const query = params.toString();
    return `/events/${encodeURIComponent(eventId)}${query ? `?${query}` : ''}`;
}

export function modalityLabel(modality, platform) {
    const label = MODALITY_LABELS[modality] || (modality ? String(modality).replace(/_/g, ' ') : '');
    const via = PLATFORM_LABELS[platform];
    return via && modality !== 'in_person' ? `${label} · ${via}` : label;
}

/** "09:30" → "9:30 am". Anything unparseable is returned as given. */
export function formatClock(value) {
    const match = /^(\d{1,2}):(\d{2})/.exec(String(value || ''));
    if (!match) return value || '';
    const hours = Number(match[1]);
    const suffix = hours >= 12 ? 'pm' : 'am';
    return `${((hours + 11) % 12) + 1}:${match[2]} ${suffix}`;
}

/** The short zone name ("AEDT") of an IANA zone on a given local date, or the zone id. */
export function zoneAbbreviation(timeZone, localDate) {
    if (!timeZone) return '';
    try {
        const at = localDate ? new Date(`${localDate}T12:00:00Z`) : new Date();
        // The viewer's locale names zones the way they read them (en-AU: "AEDT").
        const part = new Intl.DateTimeFormat(undefined, { timeZone, timeZoneName: 'short' })
            .formatToParts(at).find((item) => item.type === 'timeZoneName');
        return part?.value || timeZone;
    } catch {
        return timeZone;
    }
}

/**
 * The event's own calendar date, formatted without ever converting it through
 * the browser's zone: the server already resolved it in the event timezone.
 */
export function formatLocalDate(localDate, options = { weekday: 'short', day: 'numeric', month: 'short' }) {
    if (!localDate) return '';
    const date = new Date(`${localDate}T00:00:00Z`);
    if (Number.isNaN(date.getTime())) return localDate;
    return date.toLocaleDateString('en-GB', { ...options, timeZone: 'UTC' });
}

/** "Thu 6 Nov · 9:00 am (AEDT)" — or "Today · 9:00 am (AEDT)" on the event day. */
export function formatEventWhen(timing, { todayLabel = true } = {}) {
    if (!timing?.local_date) return 'Date to be confirmed';
    const day = todayLabel && timing.relation === 'event_day' ? 'Today' : formatLocalDate(timing.local_date);
    const time = timing.start_time ? ` · ${formatClock(timing.start_time)}` : '';
    const zone = zoneAbbreviation(timing.timezone, timing.local_date);
    return `${day}${time}${zone ? ` (${zone})` : ''}`;
}

export function countdownLabel(daysUntil) {
    if (daysUntil === null || daysUntil === undefined) return '';
    if (daysUntil <= 0) return 'Today';
    if (daysUntil === 1) return 'Tomorrow';
    return `In ${daysUntil} days`;
}

/** The Needs-attention sentence for one server item. */
export function attentionText(item) {
    const count = Number(item?.count || 0);
    if (item?.code === 'follow_ups_overdue') return `${count} follow-up${count === 1 ? '' : 's'} overdue`;
    if (item?.code === 'attendance_unmarked') return `${count} event${count === 1 ? ' needs' : 's need'} attendance marked`;
    if (item?.code === 'not_invited') return `${count} contact${count === 1 ? '' : 's'} not yet invited`;
    return `${count} ${String(item?.code || '').replace(/_/g, ' ')}`;
}

export function initials(name) {
    const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '?';
    return parts.slice(0, 2).map((part) => part[0].toUpperCase()).join('');
}

export function percent(part, whole) {
    return whole ? Math.round((Number(part || 0) / Number(whole)) * 100) : 0;
}

/** The campaign trigger vocabulary (CampaignBuilder TRIGGER_LABELS; routes/events.py mirrors it). */
const CAMPAIGN_TRIGGER_LABELS = {
    event_registered: 'Event Registered',
    event_rsvp_confirmed: 'Event RSVP Confirmed',
    event_rsvp_declined: 'Event RSVP Declined',
    event_attended: 'Event Attended',
    event_no_show: 'Event No-show',
};

export function campaignTriggerLabel(triggerType) {
    return CAMPAIGN_TRIGGER_LABELS[triggerType] || String(triggerType || 'Trigger').replace(/_/g, ' ');
}
